// src/components/forms/FormRenderer.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, TextField, Checkbox, RadioGroup, FormControlLabel, Radio, Switch, Button,
  Typography, FormControl, Select, MenuItem, Card, CardContent, Divider, Chip, Dialog,
  DialogTitle, DialogContent, IconButton, Skeleton, Stack, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams } from 'react-router-dom';
import Services from 'src/services/Services';

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box mb={1.5} display="flex" alignItems="center" gap={1}>
      {icon}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );
}

function QuestionCard({ children }) {
  return (
    <Card elevation={0} sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
    }}>
      <CardContent sx={{ p: 2.25 }}>
        {children}
      </CardContent>
    </Card>
  );
}

export default function FormRenderer({ formJson: externalFormJson }) {
  const { campaignID, formID } = useParams();

  const parsedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
  }, []);
  const token = parsedUser?.token;

  const [cities, setCities] = useState([]);
  const [towns, setTowns] = useState({});
  const [answers, setAnswers] = useState({});
  const [formJson, setFormJson] = useState(externalFormJson || null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [campaignTitle, setCampaignTitle] = useState("");

  const [legalOpen, setLegalOpen] = useState(false);
  const [activeLegal, setActiveLegal] = useState({ title: '', content: '' });

  const handleChange = useCallback((qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  }, []);
  const labelsSafe = (lbl) => (Array.isArray(lbl) ? {} : (lbl || {}));

  const normalizeRemoteForm = useCallback((remote) => {
    if (!remote) return null;
    const toStrId = (v, fallback) => {
      if (v === undefined || v === null || v === '') return String(fallback);
      return String(v);
    };

    const questionsArr =
      Array.isArray(remote?.formElements?.questions)
        ? remote.formElements.questions
        : Array.isArray(remote?.formElements)
          ? remote.formElements
          : [];

    const statusesArr =
      Array.isArray(remote?.formElements?.statuses)
        ? remote.formElements.statuses
        : Array.isArray(remote?.statuses)
          ? remote.statuses
          : [];

    return {
      id: String(remote.formID ?? remote.id ?? ''),
      title: remote.title || 'Form',
      description: remote.description || '',
      legalContent: Array.isArray(remote.legalContent) ? remote.legalContent : [],
      statuses: statusesArr, // şimdilik render etmiyoruz ama future-use
      formElements: questionsArr.map((el, idx) => ({
        id: toStrId(el.questionID ?? el.id, `auto-${idx}`),
        label: el.label || '',
        type: el.type,
        options: Array.isArray(el.options) ? el.options : [],
        labels: labelsSafe(el.labels),
      })),
    };
  }, []);

  const onSubmit = () => {
    console.log('[FormRenderer:submit]', { campaignID, formID, answers });
    Services.SubmitCampaignForm(token, { answers, campaignTitle }, formID, campaignID)
      .then((res) => {
        console.log("Submit form result:", res.data)
        if (res?.data?.status) {
          // success handling (toast, navigate, etc.)
        }
      })
      .catch((err) => console.log('[FormRenderer:SubmitForm][error]', err));
  };

  useEffect(() => {
    if (!token) return;
    Services.GetCities()
      .then((res) => {
        if (res?.data?.status) setCities(res.data.result || []);
      })
      .catch((err) => console.log('[FormRenderer:GetCities][error]', err));
  }, [token]);

  const getTowns = (cityCode) => {
    if (!token || !cityCode || towns[cityCode]) return;
    Services.GetTowns(cityCode)
      .then((res) => {
        if (res?.data?.status) {
          setTowns(prev => ({ ...prev, [cityCode]: res.data.result || [] }));
        }
      })
      .catch((err) => console.log('[FormRenderer:GetTowns][error]', err));
  };

  useEffect(() => {
    if (!token || !campaignID || !formID || externalFormJson) return;

    setIsFetching(true);
    setFetchError(null);

    Services.GetCampaignForm(token, campaignID, formID)
      .then((res) => {
        console.log('[FormRenderer:getForm][raw]', res?.data);

        const list = Array.isArray(res?.data?.result) ? res.data.result : [];
        const remoteForm =
          list.find(f => String(f.formID) === String(formID)) ||
          list[0] ||
          null;

        setCampaignTitle(list[0].formElements.campaignTitle)
        if (!remoteForm) {
          setFetchError('Form bulunamadı.');
          return;
        }

        const normalized = normalizeRemoteForm(remoteForm);

        if (normalized?.formElements?.length) {
          setFormJson(normalized);
        } else {
          setFetchError('Form içerik bulunamadı (formElements boş).');
        }
      })
      .catch((err) => {
        console.error('[FormRenderer:getForm][error]', err);
        setFetchError(err?.message || 'Form çekilirken hata oluştu');
      })
      .finally(() => setIsFetching(false));
  }, [token, campaignID, formID, externalFormJson, normalizeRemoteForm]);

  const activeForm = formJson;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      {/* Başlık Kartı */}
      <Card elevation={0} sx={{
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background:
          'linear-gradient(180deg, rgba(250,250,250,1) 0%, rgba(255,255,255,1) 100%)',
      }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {activeForm?.title || (isFetching ? <Skeleton width={220} /> : 'Form')}
            </Typography>
            {activeForm?.description && (
              <Tooltip title="Açıklama">
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            )}
          </Box>
          {activeForm?.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              {activeForm.description}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* İçerik */}
      {isFetching && !activeForm && (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Skeleton height={72} />
          <Skeleton height={72} />
          <Skeleton height={72} />
        </Stack>
      )}

      {fetchError && (
        <QuestionCard>
          <Typography color="error" variant="body2">{fetchError}</Typography>
        </QuestionCard>
      )}

      {Array.isArray(activeForm?.formElements) && activeForm.formElements.map((q, idx) => (
        <Box key={q.id} mb={2.25}>
          <QuestionCard>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {idx + 1}. {q.label}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            {q.type === 'short' && (
              <TextField
                fullWidth
                placeholder="Cevabınızı yazın"
                value={answers[q.id] ?? ''}
                onChange={e => handleChange(q.id, e.target.value)}
              />
            )}

            {q.type === 'long' && (
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Cevabınızı yazın"
                value={answers[q.id] ?? ''}
                onChange={e => handleChange(q.id, e.target.value)}
              />
            )}

            {q.type === 'multiselect' && (
              <Box mt={0.5} display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={0.75}>
                {q.options.map(opt => {
                  const checked = (answers[q.id] || []).includes(opt);
                  return (
                    <Card
                      key={opt}
                      variant="outlined"
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: checked ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={e => {
                              const prev = answers[q.id] || [];
                              const next = e.target.checked ? [...prev, opt] : prev.filter(o => o !== opt);
                              handleChange(q.id, next);
                            }}
                          />
                        }
                        label={opt}
                      />
                    </Card>
                  );
                })}
              </Box>
            )}

            {q.type === 'single' && (
              <RadioGroup
                value={answers[q.id] || ''}
                onChange={e => handleChange(q.id, e.target.value)}
              >
                <Box mt={0.5} display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={0.75}>
                  {q.options.map(opt => (
                    <Card key={opt} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                      <FormControlLabel control={<Radio />} value={opt} label={opt} />
                    </Card>
                  ))}
                </Box>
              </RadioGroup>
            )}

            {q.type === 'yesno' && (
              <Box mt={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={answers[q.id] === true}
                      onChange={e => handleChange(q.id, e.target.checked)}
                    />
                  }
                  label={answers[q.id]
                    ? (q.labels?.yesLabel || 'Evet')
                    : (q.labels?.noLabel || 'Hayır')}
                />
              </Box>
            )}

            {q.type === 'dropdown' && (
              <FormControl fullWidth>
                <Select
                  value={answers[q.id] || ''}
                  onChange={e => handleChange(q.id, e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Seçim yapın</MenuItem>
                  {q.options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {q.type === 'citydistrict' && (
              <Box display="flex" gap={1.5} width="100%">
                <FormControl fullWidth>
                  <Select
                    value={answers[q.id]?.cityCode || ''}
                    onChange={e => {
                      const selectedCityCode = e.target.value;
                      const selectedCityName = cities.find(c => c.code === selectedCityCode)?.name || '';
                      handleChange(q.id, { cityCode: selectedCityCode, cityName: selectedCityName, townCode: '', townName: '' });
                      getTowns(selectedCityCode);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>İl seçin</MenuItem>
                    {Array.isArray(cities) && cities.map(city => (
                      <MenuItem key={city.code} value={city.code}>{city.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth disabled={!answers[q.id]?.cityCode}>
                  <Select
                    value={answers[q.id]?.townCode || ''}
                    onChange={e => {
                      const selectedTownCode = e.target.value;
                      const selectedTownName = (towns[answers[q.id]?.cityCode] || []).find(t => t.code === selectedTownCode)?.name || '';
                      handleChange(q.id, { ...answers[q.id], townCode: selectedTownCode, townName: selectedTownName });
                    }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>İlçe seçin</MenuItem>
                    {Array.isArray(towns[answers[q.id]?.cityCode]) &&
                      towns[answers[q.id]?.cityCode].map(town => (
                        <MenuItem key={town.code} value={town.code}>{town.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </QuestionCard>
        </Box>
      ))}

      <Box mt={4} display="flex" justifyContent="center">
        <Button variant="contained" size="large" onClick={onSubmit}>
          Gönder
        </Button>
      </Box>

      {Array.isArray(activeForm?.legalContent) && activeForm.legalContent.length > 0 && (
        <Box mt={4}>
          <SectionHeader title="Hukuki Metinler" subtitle="Başlığa tıklayarak içeriği görüntüleyebilirsiniz." />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {activeForm.legalContent.map((lc, idx) => (
              <Chip
                key={idx}
                label={lc.title || `Metin ${idx + 1}`}
                variant="outlined"
                onClick={() => { setActiveLegal({ title: lc.title, content: lc.content }); setLegalOpen(true); }}
                sx={{
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Dialog open={legalOpen} onClose={() => setLegalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          {activeLegal.title || 'Hukuki Metin'}
          <IconButton
            aria-label="close"
            onClick={() => setLegalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div
            style={{ lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: activeLegal.content || '' }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}